import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AgGridReact } from "ag-grid-react";
import { themeQuartz } from "ag-grid-community";
import type { GridApi, GridReadyEvent, SelectionChangedEvent } from "ag-grid-community";
import {
    LineChart,
    Line,
    ResponsiveContainer,
    XAxis,
    YAxis,
    Tooltip,
    ReferenceLine,
} from "recharts";
import Plot from "react-plotly.js";
import {
    DIRECTION_COLOR,
    DirectionMark,
    directionOf,
    portfolioColumnDefs,
    type PortfolioRow,
} from "./PortfolioColumns";
import ax from "../../config/axios";
import { takeBootHash } from "../../services/deepLink";

const agTheme = themeQuartz.withParams({
    columnBorder: { style: "solid", width: 1, color: "#e0e0e0" },
    headerColumnBorder: false,
    // Outer border lives on the shell so we can pad below the last row.
    wrapperBorder: false,
    oddRowBackgroundColor: "#f8f9fa",
    spacing: 4,
    rowHeight: 30,
    headerHeight: 32,
    fontSize: 12,
    selectedRowBackgroundColor: "#cfe2ff",
});

// Single-row selection driven by clicking anywhere on the row (no checkboxes).
const rowSelection = {
    mode: "singleRow",
    checkboxes: false,
    enableClickSelection: true,
} as const;

// Detail-pane series colors — match the COT analysis rank-card sparklines.
const MM_NET_COLOR = "#0d6efd";
const CANDLE_UP = "#198754";
const CANDLE_DOWN = "#dc3545";

// Stable references so Plotly isn't handed brand-new prop objects each render.
const plotConfig = { responsive: true, displayModeBar: false } as const;
const candleLayout: Partial<Plotly.Layout> = {
    autosize: true,
    // Roughly matches the Recharts margins above (YAxis width 52).
    margin: { l: 52, r: 8, t: 4, b: 32 },
    showlegend: false,
    paper_bgcolor: "rgba(0,0,0,0)",
    plot_bgcolor: "rgba(0,0,0,0)",
    font: { size: 10, color: "#666" },
    xaxis: {
        type: "date",
        tickformat: "%m/%d/%Y",
        nticks: 4,
        rangeslider: { visible: false },
    },
    yaxis: { tickformat: ".2f", zerolinecolor: "#ccc" },
};

// The two detail-pane charts each take half the space left over after the
// header and stats block.
const CHART_PANE_STYLE: React.CSSProperties = {
    flex: "1 1 0",
    minHeight: 0,
    overflow: "hidden",
};

const parseIsoDate = (d: string) => {
    const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(d);
    if (!match) return new Date(d);
    return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
};

const fmtFullDate = (d: string) =>
    parseIsoDate(d).toLocaleDateString(undefined, {
        month: "2-digit",
        day: "2-digit",
        year: "numeric",
    });

// A single point in a root's MM net history (used for the detail chart).
interface PortfolioHistoryPoint {
    date: string;
    MM_Net: number | null;
    open: number | null;
    high: number | null;
    low: number | null;
    close: number | null;
}

// Row shape returned by /data/cot-portfolio. The backend pre-computes roll
// timing, spread contracts, and percentile fields; the table maps these through.
interface PortfolioApiRow {
    root: string;
    name: string;
    symbol: string;
    next_symbol: string;
    spread_symbol: string | null;
    latest_mm: number;
    mm_net_change: number;
    days_until_roll_start: number | null;
    roll_start_date: string | null;
    roll_end_date: string | null;
    mm_max_long: number;
    mm_max_short: number;
    latest_oi: number;
    latest_oi_date: string | null;
    mm_pct_relative: number;
    /** Side %: |latest| / |same-side historical extreme| × 100. */
    mm_side_pct: number;
    mm_pct_oi: number;
    mm_net_pctile: number;
    mm_pct_oi_pctile: number | null;
    data: PortfolioHistoryPoint[];
}

const StatItem = ({
    label,
    value,
    hint,
}: {
    label: string;
    value: React.ReactNode;
    hint?: string;
}) => (
    <div className="col">
        <div className="text-muted text-uppercase" style={{ fontSize: "0.65rem", letterSpacing: "0.03em" }}>
            {label}
        </div>
        <div className="fw-semibold" style={{ fontVariantNumeric: "tabular-nums" }}>
            {value}
            {hint && (
                <span className="text-muted fw-normal ms-1" style={{ fontSize: "0.7rem" }}>
                    {hint}
                </span>
            )}
        </div>
    </div>
);

// One series per chart so each plot gets a usable aspect ratio in the
// narrow detail pane. The section label names the series; no in-chart legend.
const HistoryChart = ({
    data,
    dataKey,
    name,
    color,
    type,
    tickFormatter,
    showZeroLine = false,
}: {
    data: PortfolioHistoryPoint[];
    dataKey: "MM_Net";
    name: string;
    color: string;
    type: "stepAfter" | "monotone";
    tickFormatter?: (v: number) => string;
    showZeroLine?: boolean;
}) => (
    <ResponsiveContainer width="100%" height="100%">
        <LineChart data={data} margin={{ top: 4, right: 8, bottom: 4, left: 4 }}>
            <XAxis
                dataKey="date"
                tick={{ fontSize: 10 }}
                tickFormatter={fmtFullDate}
                minTickGap={48}
            />
            <YAxis
                width={52}
                tick={{ fontSize: 10 }}
                domain={["dataMin", "dataMax"]}
                tickFormatter={tickFormatter}
            />
            <Tooltip
                labelFormatter={(label) => fmtFullDate(String(label))}
                formatter={(value) => [
                    value == null ? "—" : Number(value).toLocaleString(),
                    name,
                ]}
                contentStyle={{
                    fontSize: "12px",
                    padding: "2px 6px",
                    borderRadius: "4px",
                }}
                itemStyle={{ margin: 0, lineHeight: 1.2, padding: 0 }}
            />
            <Line
                type={type}
                dataKey={dataKey}
                name={name}
                stroke={color}
                strokeWidth={2}
                dot={false}
                // MM net is weekly; the daily spread rows in between are null.
                connectNulls
                isAnimationActive={false}
            />
            {showZeroLine && <ReferenceLine y={0} stroke="gray" strokeDasharray="3 3" />}
        </LineChart>
    </ResponsiveContainer>
);

// Recharts has no candlestick primitive, so the spread OHLC uses Plotly like
// the other price charts in the app. Absolutely positioned inside a relative
// parent so its height comes from the pane rather than feeding back into it.
const SpreadCandleChart = ({ data }: { data: PortfolioHistoryPoint[] }) => (
    <Plot
        data={[
            {
                type: "candlestick",
                name: "Spread",
                x: data.map((p) => p.date),
                // NaN leaves a gap for days the spread didn't trade.
                open: data.map((p) => p.open ?? NaN),
                high: data.map((p) => p.high ?? NaN),
                low: data.map((p) => p.low ?? NaN),
                close: data.map((p) => p.close ?? NaN),
                increasing: { line: { color: CANDLE_UP, width: 1 } },
                decreasing: { line: { color: CANDLE_DOWN, width: 1 } },
            },
        ]}
        layout={candleLayout}
        config={plotConfig}
        useResizeHandler
        style={{ position: "absolute", inset: 0 }}
    />
);

// Small uppercase rule used to head each pane section (filters rail, detail pane).
const SectionLabel = ({
    children,
    action,
}: {
    children: React.ReactNode;
    action?: React.ReactNode;
}) => (
    <div className="d-flex align-items-baseline justify-content-between gap-2">
        <span
            className="text-muted text-uppercase fw-semibold"
            style={{ fontSize: "0.65rem", letterSpacing: "0.06em" }}
        >
            {children}
        </span>
        {action}
    </div>
);

// Resets a numeric filter back to "unset". Rendered only when a value is present
// so the rail stays quiet at rest.
const ClearButton = ({ onClick }: { onClick: () => void }) => (
    <button
        type="button"
        className="btn btn-link btn-sm p-0 text-decoration-none"
        style={{ fontSize: "0.7rem" }}
        onClick={onClick}
    >
        clear
    </button>
);

type MetricMode = "raw" | "pctile";
type JoinOp = "and" | "or";

// Raw vs historical percentile for a positioning threshold. Flanking labels
// make the switch readable in the narrow filter rail.
const MetricModeToggle = ({
    id,
    mode,
    onChange,
}: {
    id: string;
    mode: MetricMode;
    onChange: (mode: MetricMode) => void;
}) => (
    <div className="d-flex align-items-center gap-1 flex-shrink-0">
        <span
            className={mode === "raw" ? "fw-semibold" : "text-muted"}
            style={{ fontSize: "0.65rem" }}
        >
            raw
        </span>
        <div className="form-check form-switch mb-0 p-0">
            <input
                className="form-check-input m-0"
                type="checkbox"
                role="switch"
                id={id}
                checked={mode === "pctile"}
                onChange={(e) => onChange(e.target.checked ? "pctile" : "raw")}
                style={{ cursor: "pointer" }}
            />
        </div>
        <span
            className={mode === "pctile" ? "fw-semibold" : "text-muted"}
            style={{ fontSize: "0.65rem" }}
        >
            %-ile
        </span>
    </div>
);

// Shared ≥ threshold input used by both positioning metrics (MM % of OI and
// |Historical pct. of range|). Empty means the filter is off.
const ThresholdFilter = ({
    id,
    label,
    threshold,
    onThresholdChange,
    mode,
    onModeChange,
}: {
    id: string;
    label: string;
    threshold: number | "";
    onThresholdChange: (v: number | "") => void;
    mode: MetricMode;
    onModeChange: (mode: MetricMode) => void;
}) => (
    <div>
        <div className="d-flex align-items-baseline justify-content-between gap-2 mb-1">
            <label htmlFor={id} className="mb-0 fw-semibold" style={{ fontSize: "0.8rem" }}>
                {label}
            </label>
            {threshold !== "" && <ClearButton onClick={() => onThresholdChange("")} />}
        </div>
        <div className="d-flex justify-content-end mb-1">
            <MetricModeToggle id={`${id}-mode`} mode={mode} onChange={onModeChange} />
        </div>
        <div className="input-group input-group-sm">
            <span className="input-group-text">&ge;</span>
            <input
                id={id}
                type="number"
                className="form-control text-end"
                value={threshold}
                onChange={(e) =>
                    onThresholdChange(e.target.value === "" ? "" : Number(e.target.value))
                }
            />
            <span className="input-group-text">{mode === "pctile" ? "%-ile" : "%"}</span>
        </div>
    </div>
);

// AND/OR join between the two positioning filters. Defaults to OR so the
// grouping reads as A ∧ (B ∨ C) until the user flips it.
const JoinToggle = ({
    value,
    onChange,
}: {
    value: JoinOp;
    onChange: (op: JoinOp) => void;
}) => (
    <div className="d-flex align-items-center gap-2">
        <div className="flex-grow-1 border-top" />
        <div className="btn-group btn-group-sm" role="group" aria-label="Combine positioning filters">
            <input
                type="radio"
                className="btn-check"
                name="positioningJoin"
                id="join-and"
                autoComplete="off"
                checked={value === "and"}
                onChange={() => onChange("and")}
            />
            <label className="btn btn-outline-secondary py-0" htmlFor="join-and">
                AND
            </label>
            <input
                type="radio"
                className="btn-check"
                name="positioningJoin"
                id="join-or"
                autoComplete="off"
                checked={value === "or"}
                onChange={() => onChange("or")}
            />
            <label className="btn btn-outline-secondary py-0" htmlFor="join-or">
                OR
            </label>
        </div>
        <div className="flex-grow-1 border-top" />
    </div>
);

// null = filter unset (does not constrain). false = value missing or below the
// threshold. Callers combine the two positioning results with AND/OR.
const passesThreshold = (
    value: number | null | undefined,
    threshold: number | "",
): boolean | null => {
    if (threshold === "") return null;
    if (value == null) return false;
    return value >= threshold;
};

const combineOptional = (a: boolean | null, b: boolean | null, join: JoinOp): boolean => {
    if (a == null && b == null) return true;
    if (a == null) return b!;
    if (b == null) return a;
    return join === "and" ? a && b : a || b;
};

const unsetToNull = (v: number | ""): number | null => (v === "" ? null : v);

const MAX_FILTER_PRESETS = 20;

type FilterState = {
    daysAwayFromRoll: number | null;
    mmPctOi: { threshold: number | null; mode: MetricMode };
    historicalPctOfRange: { threshold: number | null; mode: MetricMode };
    positioningJoin: JoinOp;
};

type FilterPreset = {
    name: string;
    isDefault: boolean;
    state: FilterState;
    updatedAt: string;
};

const OPEN_FILTER_STATE: FilterState = {
    daysAwayFromRoll: null,
    mmPctOi: { threshold: null, mode: "raw" },
    historicalPctOfRange: { threshold: null, mode: "raw" },
    positioningJoin: "or",
};

const snapshotFilterState = (
    daysAwayFromRoll: number | "",
    mmPctOiThreshold: number | "",
    mmPctOiMode: MetricMode,
    relPctThreshold: number | "",
    relPctMode: MetricMode,
    positioningJoin: JoinOp,
): FilterState => ({
    daysAwayFromRoll: unsetToNull(daysAwayFromRoll),
    mmPctOi: {
        threshold: unsetToNull(mmPctOiThreshold),
        mode: mmPctOiMode,
    },
    historicalPctOfRange: {
        threshold: unsetToNull(relPctThreshold),
        mode: relPctMode,
    },
    positioningJoin,
});

const itemPassesFilters = (
    item: PortfolioApiRow,
    daysAwayFromRoll: number | "",
    mmPctOiThreshold: number | "",
    mmPctOiMode: MetricMode,
    relPctThreshold: number | "",
    relPctMode: MetricMode,
    positioningJoin: JoinOp,
): boolean => {
    // A ∧ (B ∧/∨ C): roll proximity always applies; the two positioning
    // thresholds combine with the AND/OR toggle. An empty threshold drops out
    // of the grouping so A alone still works. Markets with an unknown roll
    // window cannot satisfy A once a day range is set.
    const passesRoll =
        daysAwayFromRoll === ""
            ? true
            : item.roll_start_date != null &&
              item.days_until_roll_start != null &&
              Math.abs(item.days_until_roll_start) <= daysAwayFromRoll;

    const mmPctOiValue =
        mmPctOiMode === "pctile" ? item.mm_pct_oi_pctile : item.mm_pct_oi;
    const relPctValue =
        relPctMode === "pctile"
            ? item.mm_net_pctile
            : Math.abs(item.mm_pct_relative);

    return (
        passesRoll &&
        combineOptional(
            passesThreshold(mmPctOiValue, mmPctOiThreshold),
            passesThreshold(relPctValue, relPctThreshold),
            positioningJoin,
        )
    );
};

const itemPassesFilterState = (item: PortfolioApiRow, state: FilterState) =>
    itemPassesFilters(
        item,
        state.daysAwayFromRoll ?? "",
        state.mmPctOi?.threshold ?? "",
        state.mmPctOi?.mode ?? "raw",
        state.historicalPctOfRange?.threshold ?? "",
        state.historicalPctOfRange?.mode ?? "raw",
        state.positioningJoin ?? "or",
    );

const findMarketByRoot = (data: PortfolioApiRow[], root: string) =>
    data.find((item) => item.root === root) ??
    data.find((item) => item.root.toLowerCase() === root.toLowerCase());

const readRootFromUrl = () => {
    // The live hash wins; the boot hash only covers the case where a redirect
    // (login, release notes, scope check) dropped the fragment before this page
    // could mount. It is taken either way so it can only apply to a first load.
    const bootHash = takeBootHash(window.location.pathname);
    const hash = window.location.hash || bootHash;
    const raw = decodeURIComponent(hash.replace(/^#/, "")).trim();
    if (!raw || raw.includes("/") || raw.includes("=")) return null;
    return raw;
};

// Hash + replaceState: this is a location on the page, not a new route, so
// React Router never navigates and permissions are not re-fetched.
const writeRootToUrl = (root: string | null) => {
    const url = new URL(window.location.href);
    url.hash = root ? encodeURIComponent(root) : "";
    const next = `${url.pathname}${url.search}${url.hash}`;
    const current = `${window.location.pathname}${window.location.search}${window.location.hash}`;
    if (next === current) return;
    window.history.replaceState(window.history.state, "", next);
};

const filterStatesEqual = (a?: FilterState | null, b?: FilterState | null) => {
    if (a == null || b == null) return false;
    return (
        a.daysAwayFromRoll === b.daysAwayFromRoll &&
        a.positioningJoin === b.positioningJoin &&
        a.mmPctOi?.threshold === b.mmPctOi?.threshold &&
        a.mmPctOi?.mode === b.mmPctOi?.mode &&
        a.historicalPctOfRange?.threshold === b.historicalPctOfRange?.threshold &&
        a.historicalPctOfRange?.mode === b.historicalPctOfRange?.mode
    );
};

const starPreset = (presets: FilterPreset[], name: string): FilterPreset[] =>
    presets.map((p) => ({ ...p, isDefault: p.name === name }));

const userMessageFromError = (err: unknown, fallback: string) => {
    if (typeof err === "object" && err !== null && "response" in err) {
        const message = (
            err as { response?: { data?: { user_message?: string } } }
        ).response?.data?.user_message;
        if (message) return message;
    }
    return fallback;
};

const mapApiPreset = (p: {
    name: string;
    isDefault?: boolean;
    state: FilterState;
    updatedAt?: string;
}): FilterPreset => ({
    name: p.name,
    isDefault: Boolean(p.isDefault),
    state: p.state,
    updatedAt: p.updatedAt ?? "",
});

type ApiPreset = Parameters<typeof mapApiPreset>[0];

const FilterPresetPanel = ({
    presets,
    activePresetName,
    isDirty,
    isBusy,
    onSelect,
    onStarActive,
    onDeleteActive,
    onSave,
    onSaveAs,
    onReset,
}: {
    presets: FilterPreset[];
    activePresetName: string | null;
    isDirty: boolean;
    isBusy: boolean;
    onSelect: (name: string) => void;
    onStarActive: () => void;
    onDeleteActive: () => void;
    onSave: () => void;
    onSaveAs: (name: string) => Promise<string | null>;
    onReset: () => void;
}) => {
    const [isSavingAs, setIsSavingAs] = useState(false);
    const [saveAsName, setSaveAsName] = useState("");
    const [saveAsError, setSaveAsError] = useState<string | null>(null);
    const [saveAsBusy, setSaveAsBusy] = useState(false);

    const atCap = presets.length >= MAX_FILTER_PRESETS;
    const hasActive = activePresetName != null;
    const activeIsDefault =
        presets.find((p) => p.name === activePresetName)?.isDefault ?? false;

    const startSaveAs = () => {
        setIsSavingAs(true);
        setSaveAsName("");
        setSaveAsError(null);
    };

    const cancelSaveAs = () => {
        setIsSavingAs(false);
        setSaveAsName("");
        setSaveAsError(null);
        setSaveAsBusy(false);
    };

    const confirmSaveAs = async () => {
        setSaveAsBusy(true);
        const error = await onSaveAs(saveAsName);
        setSaveAsBusy(false);
        if (error) {
            setSaveAsError(error);
            return;
        }
        cancelSaveAs();
    };

    return (
        <div className="d-flex flex-column gap-2">
            <div className="d-flex align-items-baseline justify-content-between gap-2">
                <span className="fw-semibold" style={{ fontSize: "0.8rem" }}>
                    Profile
                </span>
                {hasActive && isDirty && (
                    <span className="text-muted" style={{ fontSize: "0.7rem" }}>
                        unsaved
                    </span>
                )}
            </div>
            <div className="d-flex gap-1">
                <select
                    className={`form-select form-select-sm${!hasActive && presets.length > 0 ? " text-muted" : ""}`}
                    aria-label="Filter profile"
                    value={activePresetName ?? ""}
                    disabled={presets.length === 0 || isBusy}
                    onChange={(e) => {
                        if (e.target.value) onSelect(e.target.value);
                    }}
                >
                    {presets.length === 0 ? (
                        <option value="">No profiles yet</option>
                    ) : (
                        <>
                            {!hasActive && (
                                <option value="" disabled>
                                    Select filter profile
                                </option>
                            )}
                            {presets.map((p) => (
                                <option key={p.name} value={p.name}>
                                    {p.isDefault ? "★ " : ""}
                                    {p.name}
                                </option>
                            ))}
                        </>
                    )}
                </select>
                <button
                    type="button"
                    className="btn btn-outline-secondary btn-sm px-2"
                    disabled={!hasActive || activeIsDefault || isBusy}
                    title={
                        activeIsDefault
                            ? "This is the default profile"
                            : "Set as default"
                    }
                    aria-label="Set as default profile"
                    onClick={onStarActive}
                >
                    <i
                        className={`bi ${
                            activeIsDefault ? "bi-star-fill text-warning" : "bi-star"
                        }`}
                        aria-hidden="true"
                    />
                </button>
                <button
                    type="button"
                    className="btn btn-outline-danger btn-sm px-2"
                    disabled={!hasActive || isBusy}
                    title="Delete profile"
                    aria-label="Delete profile"
                    onClick={onDeleteActive}
                >
                    <i className="bi bi-trash" aria-hidden="true" />
                </button>
            </div>
            {isSavingAs ? (
                <div className="d-flex flex-column gap-1">
                    <input
                        type="text"
                        className="form-control form-control-sm"
                        placeholder="Profile name"
                        value={saveAsName}
                        autoFocus
                        onChange={(e) => {
                            setSaveAsName(e.target.value);
                            if (saveAsError) setSaveAsError(null);
                        }}
                        onKeyDown={(e) => {
                            if (e.key === "Enter") confirmSaveAs();
                            if (e.key === "Escape") cancelSaveAs();
                        }}
                    />
                    {saveAsError && (
                        <div className="text-danger" style={{ fontSize: "0.7rem" }}>
                            {saveAsError}
                        </div>
                    )}
                    <div className="d-flex gap-1">
                        <button
                            type="button"
                            className="btn btn-outline-secondary btn-sm flex-grow-1"
                            disabled={saveAsBusy}
                            onClick={cancelSaveAs}
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            className="btn btn-primary btn-sm flex-grow-1"
                            disabled={saveAsBusy}
                            onClick={confirmSaveAs}
                        >
                            Create
                        </button>
                    </div>
                </div>
            ) : (
                <div className="d-flex gap-1">
                    <button
                        type="button"
                        className="btn btn-primary btn-sm flex-grow-1"
                        disabled={!hasActive || !isDirty || isBusy}
                        title={
                            !hasActive
                                ? "Create a profile first"
                                : isDirty
                                  ? "Overwrite this profile"
                                  : "No changes to save"
                        }
                        onClick={onSave}
                    >
                        Save
                    </button>
                    <button
                        type="button"
                        className="btn btn-outline-primary btn-sm flex-grow-1"
                        disabled={atCap || isBusy}
                        title={
                            atCap
                                ? `Maximum ${MAX_FILTER_PRESETS} profiles`
                                : "Save as a new profile"
                        }
                        onClick={startSaveAs}
                    >
                        Save as
                    </button>
                </div>
            )}
            {hasActive && isDirty && !isSavingAs && (
                <div className="d-flex gap-1">
                    <div className="flex-grow-1">
                        <button
                            type="button"
                            className="btn btn-link btn-sm p-0 text-decoration-none"
                            style={{ fontSize: "0.7rem" }}
                            disabled={isBusy}
                            title="Revert filters to the saved profile"
                            onClick={onReset}
                        >
                            reset
                        </button>
                    </div>
                    <span className="flex-grow-1" />
                </div>
            )}
        </div>
    );
};

const COTPortfolio = () => {
    const [daysAwayFromRoll, setDaysAwayFromRoll] = useState<number | "">(10);
    const [mmPctOiThreshold, setMmPctOiThreshold] = useState<number | "">("");
    const [mmPctOiMode, setMmPctOiMode] = useState<MetricMode>("raw");
    const [relPctThreshold, setRelPctThreshold] = useState<number | "">("");
    const [relPctMode, setRelPctMode] = useState<MetricMode>("raw");
    const [positioningJoin, setPositioningJoin] = useState<JoinOp>("or");

    const [rawData, setRawData] = useState<PortfolioApiRow[]>([]);
    const [reportDate, setReportDate] = useState<Date | null>(null);
    const [selectedRoot, setSelectedRoot] = useState<string | null>(null);
    // Survives filter changes: a checked root stays checked even if it drops
    // out of the screener and later comes back.
    const [currentlyTradingRoots, setCurrentlyTradingRoots] = useState<
        Set<string>
    >(() => new Set());
    const [filterPresets, setFilterPresets] = useState<FilterPreset[]>([]);
    const [activePresetName, setActivePresetName] = useState<string | null>(null);
    const [presetBusy, setPresetBusy] = useState(false);
    const [portfolioLoaded, setPortfolioLoaded] = useState(false);
    const [tradingLoaded, setTradingLoaded] = useState(false);
    const [profilesLoaded, setProfilesLoaded] = useState(false);
    const [screenerReady, setScreenerReady] = useState(false);
    const skipTradingPersist = useRef(true);
    const gridApiRef = useRef<GridApi<PortfolioRow> | null>(null);
    const selectedRootRef = useRef(selectedRoot);
    selectedRootRef.current = selectedRoot;

    const applyFilterState = useCallback((state: FilterState) => {
        setDaysAwayFromRoll(state.daysAwayFromRoll ?? "");
        setMmPctOiThreshold(state.mmPctOi?.threshold ?? "");
        setMmPctOiMode(state.mmPctOi?.mode ?? "raw");
        setRelPctThreshold(state.historicalPctOfRange?.threshold ?? "");
        setRelPctMode(state.historicalPctOfRange?.mode ?? "raw");
        setPositioningJoin(state.positioningJoin ?? "or");
    }, []);

    useEffect(() => {
        const controller = new AbortController();
        const { signal } = controller;

        ax.get("data/cot-portfolio", { signal })
            .then((res) => {
                setRawData(res.data.data as PortfolioApiRow[]);
                setReportDate(
                    res.data.last_update
                        ? new Date(res.data.last_update)
                        : null,
                );
            })
            .catch((err) => {
                if (err.name === "CanceledError") return;
                console.error("Error fetching COT portfolio:", err);
            })
            .finally(() => {
                if (!signal.aborted) setPortfolioLoaded(true);
            });

        ax.get("data/user-state/cot-portfolio-trading", { signal })
            .then((res) => {
                const roots = Array.isArray(res.data?.roots) ? res.data.roots : [];
                skipTradingPersist.current = true;
                setCurrentlyTradingRoots(new Set(roots));
            })
            .catch((err) => {
                if (err.name === "CanceledError") return;
                console.error("Error fetching currently trading markets:", err);
            })
            .finally(() => {
                if (!signal.aborted) setTradingLoaded(true);
            });

        ax.get("data/user-state/cot-portfolio", { signal })
            .then((res) => {
                const presets = ((res.data?.presets ?? []) as ApiPreset[]).map(
                    mapApiPreset,
                );
                setFilterPresets(presets);
            })
            .catch((err) => {
                if (err.name === "CanceledError") return;
                console.error("Error fetching filter profiles:", err);
            })
            .finally(() => {
                if (!signal.aborted) setProfilesLoaded(true);
            });

        return () => controller.abort();
    }, []);

    useEffect(() => {
        if (!portfolioLoaded || !profilesLoaded || screenerReady) return;

        const urlRoot = readRootFromUrl();
        const defaultPreset = filterPresets.find((p) => p.isDefault) ?? null;
        const market = urlRoot ? findMarketByRoot(rawData, urlRoot) : undefined;

        if (market) {
            if (
                defaultPreset &&
                itemPassesFilterState(market, defaultPreset.state)
            ) {
                setActivePresetName(defaultPreset.name);
                applyFilterState(defaultPreset.state);
            } else {
                setActivePresetName(null);
                applyFilterState(OPEN_FILTER_STATE);
            }
            setSelectedRoot(market.root);
        } else if (defaultPreset) {
            setActivePresetName(defaultPreset.name);
            applyFilterState(defaultPreset.state);
        }

        setScreenerReady(true);
    }, [
        portfolioLoaded,
        profilesLoaded,
        screenerReady,
        rawData,
        filterPresets,
        applyFilterState,
    ]);

    useEffect(() => {
        if (!screenerReady) return;
        writeRootToUrl(selectedRoot);
    }, [selectedRoot, screenerReady]);

    const rows = useMemo<PortfolioRow[]>(() => {
        return rawData
            .filter((item) =>
                itemPassesFilters(
                    item,
                    daysAwayFromRoll,
                    mmPctOiThreshold,
                    mmPctOiMode,
                    relPctThreshold,
                    relPctMode,
                    positioningJoin,
                ),
            )
            .map((item) => {
                // Net change % vs the prior report's net position. Trivial display
                // derivation from the two backend-provided values.
                const previous = item.latest_mm - item.mm_net_change;
                const netChangePct =
                    item.mm_net_change != null && previous
                        ? (item.mm_net_change / Math.abs(previous)) * 100
                        : null;

                return {
                    root: item.root,
                    name: item.name.split(" - ")[0],

                    netPosition: item.latest_mm,
                    netChange: item.mm_net_change ?? null,
                    netChangePct,

                    spreadFrom: item.symbol ?? null,
                    spreadTo: item.next_symbol ?? null,
                    spreadSymbol: item.spread_symbol ?? null,

                    daysToRoll:
                        item.days_until_roll_start != null
                            ? Math.ceil(item.days_until_roll_start)
                            : null,
                    rollStartDate: item.roll_start_date ?? null,
                    rollEndDate: item.roll_end_date ?? null,

                    relativePct: item.mm_pct_relative,
                    relativePctile: item.mm_net_pctile ?? null,

                    mmPctOi: item.mm_pct_oi,
                    mmPctOiPctile: item.mm_pct_oi_pctile ?? null,

                    stars: Math.round(Math.random() * 5), // placeholder
                } satisfies PortfolioRow;
            });
    }, [
        rawData,
        daysAwayFromRoll,
        mmPctOiThreshold,
        mmPctOiMode,
        relPctThreshold,
        relPctMode,
        positioningJoin,
    ]);

    // The full API record behind the selected row — the drill-down panels read
    // from this rather than the flattened grid row so they can use fields the
    // table doesn't show (max long/short, open interest, percentiles).
    const selectedMarket = useMemo(
        () => rawData.find((item) => item.root === selectedRoot) ?? null,
        [rawData, selectedRoot],
    );

    const chartSeries = selectedMarket?.data ?? [];

    const selectedDirection = directionOf(selectedMarket?.latest_mm);

    const selectedRollLabel =
        selectedMarket?.days_until_roll_start != null
            ? `${Math.abs(Math.ceil(selectedMarket.days_until_roll_start))}d ${
                  selectedMarket.days_until_roll_start > 0 ? "out" : "past"
              }`
            : null;

    // Nothing is selected until the user picks a row, so the drill-down panes
    // don't bias the read with a default market.
    const handleSelectionChanged = (event: SelectionChangedEvent<PortfolioRow>) => {
        const root = event.api.getSelectedRows()[0]?.root ?? null;
        // Grid fires an empty selection when it first mounts; ignore that so a
        // shared hash is not wiped before firstDataRendered can select the row.
        if (root == null) return;
        setSelectedRoot(root);
    };

    const handleGridReady = (event: GridReadyEvent<PortfolioRow>) => {
        gridApiRef.current = event.api;
    };

    const handleFirstDataRendered = () => {
        const api = gridApiRef.current;
        const root = selectedRootRef.current;
        if (!api || !root) return;
        const node = api.getRowNode(root);
        if (!node) return;
        node.setSelected(true);
        if (node.rowIndex != null) {
            api.ensureIndexVisible(node.rowIndex, "middle");
        }
    };

    useEffect(() => {
        if (!screenerReady || !selectedRoot) return;
        const stillVisible = rows.some((row) => row.root === selectedRoot);
        if (!stillVisible) {
            setSelectedRoot(null);
            gridApiRef.current?.deselectAll();
            return;
        }
        const api = gridApiRef.current;
        if (!api) return;
        const node = api.getRowNode(selectedRoot);
        if (!node || node.isSelected()) return;
        node.setSelected(true);
        if (node.rowIndex != null) {
            api.ensureIndexVisible(node.rowIndex, "middle");
        }
    }, [selectedRoot, rows, screenerReady]);

    const currentlyTradingRootsRef = useRef(currentlyTradingRoots);
    currentlyTradingRootsRef.current = currentlyTradingRoots;

    const handleCurrentlyTradingChange = useCallback(
        (root: string, trading: boolean) => {
            setCurrentlyTradingRoots((prev) => {
                if (trading === prev.has(root)) return prev;
                const next = new Set(prev);
                if (trading) next.add(root);
                else next.delete(root);
                return next;
            });
        },
        [],
    );

    useEffect(() => {
        if (!tradingLoaded) return;
        if (skipTradingPersist.current) {
            skipTradingPersist.current = false;
            return;
        }
        const timer = window.setTimeout(() => {
            ax.post("data/user-state/cot-portfolio-trading", {
                roots: [...currentlyTradingRoots].sort(),
            }).catch((err) => {
                console.error("Error saving currently trading markets:", err);
            });
        }, 400);
        return () => window.clearTimeout(timer);
    }, [currentlyTradingRoots, tradingLoaded]);

    const gridContext = useMemo(
        () => ({
            isCurrentlyTrading: (root: string) =>
                currentlyTradingRootsRef.current.has(root),
            onCurrentlyTradingChange: handleCurrentlyTradingChange,
        }),
        [handleCurrentlyTradingChange],
    );

    const currentFilterState = useMemo(
        () =>
            snapshotFilterState(
                daysAwayFromRoll,
                mmPctOiThreshold,
                mmPctOiMode,
                relPctThreshold,
                relPctMode,
                positioningJoin,
            ),
        [
            daysAwayFromRoll,
            mmPctOiThreshold,
            mmPctOiMode,
            relPctThreshold,
            relPctMode,
            positioningJoin,
        ],
    );

    const activePreset =
        filterPresets.find((p) => p.name === activePresetName) ?? null;
    const isPresetDirty =
        activePreset != null &&
        !filterStatesEqual(currentFilterState, activePreset.state);

    const handleSelectPreset = (name: string) => {
        const preset = filterPresets.find((p) => p.name === name);
        if (!preset) return;
        setActivePresetName(name);
        applyFilterState(preset.state);
    };

    const handleResetPreset = () => {
        if (!activePreset) return;
        applyFilterState(activePreset.state);
    };

    const handleStarActivePreset = async () => {
        if (!activePresetName) return;
        setPresetBusy(true);
        try {
            const res = await ax.post("data/user-state/cot-portfolio", {
                name: activePresetName,
                isDefault: true,
            });
            const updated = mapApiPreset(res.data);
            setFilterPresets((prev) => starPreset(prev, updated.name));
        } catch (err) {
            console.error("Error starring filter profile:", err);
        } finally {
            setPresetBusy(false);
        }
    };

    const handleDeleteActivePreset = async () => {
        if (!activePresetName) return;
        setPresetBusy(true);
        try {
            const res = await ax.post("data/user-state/cot-portfolio/delete", {
                name: activePresetName,
            });
            const remaining = ((res.data?.presets ?? []) as ApiPreset[]).map(
                mapApiPreset,
            );
            setFilterPresets(remaining);
            const nextName =
                res.data?.defaultName ?? remaining[0]?.name ?? null;
            setActivePresetName(nextName);
            const nextPreset = remaining.find((p) => p.name === nextName);
            if (nextPreset) applyFilterState(nextPreset.state);
        } catch (err) {
            console.error("Error deleting filter profile:", err);
        } finally {
            setPresetBusy(false);
        }
    };

    const handleSavePreset = async () => {
        if (!activePresetName) return;
        setPresetBusy(true);
        const savedState = currentFilterState;
        try {
            const res = await ax.post("data/user-state/cot-portfolio", {
                name: activePresetName,
                state: savedState,
            });
            const updated = mapApiPreset(res.data);
            setFilterPresets((prev) =>
                prev.map((p) =>
                    p.name === updated.name ? { ...updated, state: savedState } : p,
                ),
            );
        } catch (err) {
            console.error("Error saving filter profile:", err);
        } finally {
            setPresetBusy(false);
        }
    };

    const handleSaveAsPreset = async (name: string): Promise<string | null> => {
        const trimmed = name.trim();
        if (!trimmed) return "Enter a name";
        if (
            filterPresets.some(
                (p) => p.name.toLowerCase() === trimmed.toLowerCase(),
            )
        ) {
            return "A profile with that name already exists";
        }
        if (filterPresets.length >= MAX_FILTER_PRESETS) {
            return `Maximum ${MAX_FILTER_PRESETS} profiles`;
        }
        try {
            const res = await ax.post("data/user-state/cot-portfolio", {
                name: trimmed,
                state: currentFilterState,
            });
            const created = {
                ...mapApiPreset(res.data),
                state: currentFilterState,
            };
            setFilterPresets((prev) => {
                const without = prev.filter((p) => p.name !== created.name);
                const next = [...without, created];
                return created.isDefault ? starPreset(next, created.name) : next;
            });
            setActivePresetName(created.name);
            return null;
        } catch (err) {
            return userMessageFromError(err, "Could not save profile");
        }
    };

    return (
        <div className="w-100 h-100 d-flex flex-column" style={{ minHeight: 0 }}>


            <div className="card mb-2 flex-grow-1" style={{ minHeight: 0 }}>
                <div className="card-header d-flex flex-wrap align-items-baseline justify-content-between gap-2">
                    <div className="d-flex align-items-baseline gap-2" style={{ minWidth: 0 }}>
                        <h5 className="card-title mb-0">COT Portfolio Screener</h5>
                        <span className="text-muted" style={{ fontSize: "0.75rem" }}>
                            Commitments of Traders &middot; weekly
                        </span>
                    </div>
                    <span className="text-muted" style={{ fontSize: "0.75rem" }}>
                        Report:{" "}
                        {reportDate
                            ? reportDate.toLocaleDateString()
                            : "—"}
                    </span>
                </div>
                <div className="card-body h-100 p-2" style={{ minHeight: 0 }}>
                    <div className="row g-2 h-100">
                        {/* Column 1 — filter rail */}
                        <div className="col-2 h-100" style={{ minWidth: 0 }}>
                            <div
                                className="border border-1 rounded h-100 d-flex flex-column"
                                style={{ minHeight: 0 }}
                            >
                                <div className="px-2 py-2 border-bottom flex-shrink-0">
                                    <SectionLabel>Filters</SectionLabel>
                                </div>
                                <div
                                    className="p-2 d-flex flex-column gap-3 flex-grow-1"
                                    style={{ minHeight: 0, overflowY: "auto" }}
                                >
                                    <div>
                                        <div className="d-flex align-items-baseline justify-content-between gap-2 mb-1">
                                            <label
                                                htmlFor="daysAwayFromRoll"
                                                className="mb-0 fw-semibold"
                                                style={{ fontSize: "0.8rem" }}
                                            >
                                                Proximity to roll
                                            </label>
                                            {daysAwayFromRoll !== "" && (
                                                <ClearButton onClick={() => setDaysAwayFromRoll("")} />
                                            )}
                                        </div>
                                        <div className="input-group input-group-sm">
                                            <span className="input-group-text">within</span>
                                            <input
                                                id="daysAwayFromRoll"
                                                type="number"
                                                className="form-control text-end"
                                                value={daysAwayFromRoll}
                                                onChange={(e) =>
                                                    setDaysAwayFromRoll(
                                                        e.target.value === "" ? "" : Number(e.target.value),
                                                    )
                                                }
                                            />
                                            <span className="input-group-text">days</span>
                                        </div>
                                    </div>

                                    <div>
                                        <div className="fw-semibold mb-2" style={{ fontSize: "0.8rem" }}>
                                            Positioning extremity
                                        </div>
                                        <div className="d-flex flex-column gap-2">
                                            <ThresholdFilter
                                                id="mmPctOiThreshold"
                                                label="MM % of OI"
                                                threshold={mmPctOiThreshold}
                                                onThresholdChange={setMmPctOiThreshold}
                                                mode={mmPctOiMode}
                                                onModeChange={setMmPctOiMode}
                                            />
                                            <JoinToggle
                                                value={positioningJoin}
                                                onChange={setPositioningJoin}
                                            />
                                            <ThresholdFilter
                                                id="relPctThreshold"
                                                label="|Historical pct. of range|"
                                                threshold={relPctThreshold}
                                                onThresholdChange={setRelPctThreshold}
                                                mode={relPctMode}
                                                onModeChange={setRelPctMode}
                                            />
                                        </div>
                                    </div>

                                    <div className="rounded border border-primary-subtle bg-primary-subtle px-2 py-2">
                                        <div
                                            className="fw-semibold"
                                            style={{ fontSize: "1.35rem", lineHeight: 1 }}
                                        >
                                            {rows.length}
                                        </div>
                                        <div className="text-muted" style={{ fontSize: "0.7rem" }}>
                                            markets match of {rawData.length}
                                        </div>
                                    </div>

                                    <FilterPresetPanel
                                        presets={filterPresets}
                                        activePresetName={activePresetName}
                                        isDirty={isPresetDirty}
                                        isBusy={presetBusy}
                                        onSelect={handleSelectPreset}
                                        onStarActive={handleStarActivePreset}
                                        onDeleteActive={handleDeleteActivePreset}
                                        onSave={handleSavePreset}
                                        onSaveAs={handleSaveAsPreset}
                                        onReset={handleResetPreset}
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Column 2 — screener grid */}
                        <div className="col-7 h-100" style={{ minWidth: 0 }}>
                            {rows.length > 0 && tradingLoaded && screenerReady && (
                                <AgGridReact<PortfolioRow>
                                    rowData={rows}
                                    columnDefs={portfolioColumnDefs}
                                    theme={agTheme}
                                    rowHeight={48}
                                    defaultColDef={{ resizable: true, sortable: true }}
                                    // Keying rows by root preserves the selection when the
                                    // day filter re-derives the row array.
                                    getRowId={(params) => params.data.root}
                                    context={gridContext}
                                    rowSelection={rowSelection}
                                    rowStyle={{ cursor: "pointer" }}
                                    onGridReady={handleGridReady}
                                    onFirstDataRendered={handleFirstDataRendered}
                                    onSelectionChanged={handleSelectionChanged}
                                />
                            )}
                            {rows.length === 0 && portfolioLoaded && screenerReady && (
                                <div className="text-center text-muted">
                                    <p>No data found</p>
                                </div>
                            )}
                        </div>

                        {/* Column 3 — detail pane for the selected row */}
                        <div className="col-3 h-100" style={{ minWidth: 0 }}>
                            <div
                                className="border border-1 rounded h-100 d-flex flex-column"
                                style={{ minHeight: 0 }}
                            >
                                {selectedMarket ? (
                                    <>
                                        <div className="px-2 py-2 border-bottom flex-shrink-0">
                                            <div
                                                className="d-flex align-items-baseline gap-1 text-truncate"
                                                title={selectedMarket.name}
                                            >
                                                <span className="fw-semibold text-truncate">
                                                    {selectedMarket.name.split(" - ")[0]}
                                                </span>
                                                {selectedDirection && (
                                                    <>
                                                        <DirectionMark direction={selectedDirection} />
                                                        <span
                                                            className="fw-semibold"
                                                            style={{
                                                                color: DIRECTION_COLOR[selectedDirection],
                                                                fontSize: "0.8rem",
                                                            }}
                                                        >
                                                            {selectedDirection}
                                                        </span>
                                                    </>
                                                )}
                                            </div>
                                            <div
                                                className="text-muted"
                                                style={{
                                                    fontSize: "0.72rem",
                                                    fontVariantNumeric: "tabular-nums",
                                                }}
                                            >
                                                {selectedMarket.symbol} &rarr; {selectedMarket.next_symbol}
                                                {selectedRollLabel && ` \u00b7 roll ${selectedRollLabel}`}
                                            </div>
                                        </div>

                                        <div
                                            className="px-2 pt-2 flex-grow-1 d-flex flex-column gap-2"
                                            style={{ minHeight: 0 }}
                                        >
                                            {/* Zero flex-basis on both charts so they split the
                                                pane evenly instead of sizing off rendered content. */}
                                            <div className="d-flex flex-column" style={CHART_PANE_STYLE}>
                                                <SectionLabel>MM Net</SectionLabel>
                                                <div className="flex-grow-1" style={{ minHeight: 0 }}>
                                                    <HistoryChart
                                                        data={chartSeries}
                                                        dataKey="MM_Net"
                                                        name="MM Net"
                                                        color={MM_NET_COLOR}
                                                        type="stepAfter"
                                                        tickFormatter={(v) => v.toLocaleString()}
                                                        showZeroLine
                                                    />
                                                </div>
                                            </div>
                                            <div className="d-flex flex-column" style={CHART_PANE_STYLE}>
                                                <SectionLabel
                                                    action={
                                                        selectedMarket.spread_symbol ? (
                                                            <span
                                                                className="text-muted text-truncate"
                                                                title={selectedMarket.spread_symbol}
                                                                style={{
                                                                    fontSize: "0.7rem",
                                                                    fontVariantNumeric: "tabular-nums",
                                                                }}
                                                            >
                                                                {selectedMarket.spread_symbol}
                                                            </span>
                                                        ) : undefined
                                                    }
                                                >
                                                    Spread
                                                </SectionLabel>
                                                <div
                                                    className="flex-grow-1"
                                                    style={{ minHeight: 0, position: "relative" }}
                                                >
                                                    <SpreadCandleChart data={chartSeries} />
                                                </div>
                                            </div>
                                        </div>

                                        <div
                                            className="px-2 py-2 mt-2 border-top flex-shrink-0"
                                            style={{ maxHeight: "45%", overflowY: "auto" }}
                                        >
                                            <SectionLabel>Positioning Detail</SectionLabel>
                                            <div
                                                className="row row-cols-2 g-2 mt-1"
                                                style={{ fontSize: "0.8rem" }}
                                            >
                                                <StatItem
                                                    label="Max Long / Short"
                                                    value={
                                                        <>
                                                            <span className="text-success">
                                                                {selectedMarket.mm_max_long.toLocaleString()}
                                                            </span>
                                                            <span className="text-muted mx-1">/</span>
                                                            <span className="text-danger">
                                                                {selectedMarket.mm_max_short.toLocaleString()}
                                                            </span>
                                                        </>
                                                    }
                                                />
                                                <StatItem
                                                    label="Open Interest"
                                                    value={selectedMarket.latest_oi.toLocaleString()}
                                                    hint={
                                                        selectedMarket.latest_oi_date
                                                            ? new Date(
                                                                selectedMarket.latest_oi_date,
                                                            ).toLocaleDateString()
                                                            : undefined
                                                    }
                                                />
                                                <StatItem
                                                    label="MM Side %"
                                                    value={`${selectedMarket.mm_side_pct.toFixed(1)}%`}
                                                />
                                                <StatItem
                                                    label="MM Net %-ile"
                                                    value={`${selectedMarket.mm_net_pctile.toFixed(0)}%`}
                                                />
                                                <StatItem
                                                    label="MM % of OI"
                                                    value={`${selectedMarket.mm_pct_oi.toFixed(1)}%`}
                                                    hint={
                                                        selectedMarket.mm_pct_oi_pctile != null
                                                            ? `${selectedMarket.mm_pct_oi_pctile.toFixed(0)}%-ile`
                                                            : undefined
                                                    }
                                                />
                                                <StatItem
                                                    label="Roll Window"
                                                    value={
                                                        selectedMarket.roll_start_date
                                                            ? `${new Date(
                                                                selectedMarket.roll_start_date,
                                                            ).toLocaleDateString()} \u2013 ${selectedMarket.roll_end_date
                                                                ? new Date(
                                                                    selectedMarket.roll_end_date,
                                                                ).toLocaleDateString()
                                                                : "?"
                                                            }`
                                                            : "N/A"
                                                    }
                                                />
                                            </div>
                                        </div>
                                    </>
                                ) : (
                                    <div className="h-100 d-flex align-items-center justify-content-center text-center text-muted p-3">
                                        Select a market to drill in
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default COTPortfolio;
